import React, {useState} from "react";
import {Grid} from "@mui/material";
import InputField from "../common/inputs/InputField.tsx";
import {customNumberInputHandler} from "../../utils/functions/customNumberInputHandler.ts";
import DialogComponent from "../common/DialogComponent/DialogComponent.tsx";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {axiosFunction, toastMessage} from "../commonFunctions";
import OtpInput from "../common/inputs/OtpInput.tsx";
import styled from "styled-components";
import {v2ReactURL} from "../../utils/constant.ts";
// import {ErrorState} from "../../pages/Contact/Contact.tsx";
import {apiResponseHandler} from "../../utils/functions/apiResponseHandler.ts";
import {useSelector} from "react-redux";
import { ErrorState } from "../../pages/Contact/MainDataTable.tsx";

interface EditPointsDialogProps {
    editPointsDialogData: {
        open: boolean;
        mobNo?: string;
        points?: number | null | undefined;
    };
    setEditPointsDialogData: React.Dispatch<React.SetStateAction<{
        open: boolean;
        mobNo?: string;
        points?: number | null | undefined;
    }>>;
    onSuccess: () => void;
}

const EditPointSchema = z.object({
    inPoints: z.number().nullable(),
    outPoints: z.number().nullable(),
});

export type EditPointType = z.infer<typeof EditPointSchema>;

const StyledPLight = styled('p')`
    font-size: 14px;
    font-weight: 300;
    color: #888;
    margin: 0;
    padding: 5px 0 10px 0;
`;

const StyledPDark = styled('p')`
    font-size: 14px;
    font-weight: 400;
    color: #333;
    margin: 0;
    padding: 0;
`;

const EditPointsDialog: React.FC<EditPointsDialogProps> = ({
   editPointsDialogData,
   setEditPointsDialogData,
   onSuccess
}) => {

    const sessionList = useSelector((state: any) => state.sessionData.sessionScope);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
    } = useForm<EditPointType>({
        defaultValues: {
            inPoints: null,
            outPoints: null,
        },
        mode: "onChange", // Enables real-time validation
    });

    const inPoints = watch("inPoints");
    const outPoints = watch("outPoints");

    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [otp, setOtp] = useState<string>("");

    // Handle changes to ensure only one field has a value at a time
    const handleInPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {  // Allows numbers and a single dot
            if (value !== "") {
                setValue("outPoints", null);
            }
        }
    };

    const handleOutPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {  // Allows numbers and a single dot
            if (value !== "") {
                setValue("inPoints", null);
            }
        }
    };

    const sendEditPointsOtp = async (data: EditPointType) => {
        if (data.outPoints && (!editPointsDialogData.points || data.outPoints > editPointsDialogData.points)) {
            toastMessage("The points to be deducted cannot exceed the available points.");
            return;
        }
        setIsLoading(true);
        const response = await axiosFunction(`${v2ReactURL}/contact/adjust/point/otp`, "POST");

        setIsLoading(false);
        const error: ErrorState | null = apiResponseHandler(response, true);
        if (error != null) {
            toastMessage(error.message);
            return;
        }

        if (response.data.status) {
            setIsSubmit(true);
            toastMessage("Otp send successfully", "success");
        }
    }

    const saveEditPoints = async (data: EditPointType) => {
        setIsLoading(true);
        const response = await axiosFunction(`${v2ReactURL}/contact/adjust/point`, "POST", {
            inPoints: data.inPoints,
            outPoints: data.outPoints,
            otp: otp,
            mobNo: editPointsDialogData.mobNo,
        });

        setIsLoading(false);
        const error: ErrorState | null = apiResponseHandler(response, true);
        if (error != null) {
            toastMessage(error.message);
            return;
        }

        if (response.data.status) {
            toastMessage("Points has been adjusted successfully!", "success");
            onSuccess();
            handleCloseDialog();
        }
    }

    const handleCloseDialog = () => {
        reset(); // Reset form fields
        setEditPointsDialogData({open: false}); // Close modal and clear data
        setIsSubmit(false);
        setOtp("");
    };


    return (
        <DialogComponent
            handleClose={handleCloseDialog}
            dialogTitle={"Points Adjustment"}
            openDialog={editPointsDialogData.open}
            dialogContent={
                isSubmit ? (
                        <>
                            <StyledPDark>Enter OTP</StyledPDark>
                            <StyledPLight>OTP has been shared on ********{sessionList?.mobileno?.slice(-3) || 123}</StyledPLight>
                            <OtpInput separator={<span></span>} length={6} value={otp} onChange={setOtp}/>
                        </>
                    )
                    :
                    (<>
                        <Grid container>
                            <Grid item xs={12}>
                                <InputField
                                    labelName="In Points"
                                    placeholder="Enter in points"
                                    name="inPoints"
                                    id="inPoints"
                                    inputType="text"
                                    register={register}
                                    onChange={handleInPointsChange}
                                    onKeyDown={customNumberInputHandler(5, sessionList?.decimalPoint || 2)}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={12}>
                                <InputField
                                    labelName="Out Points"
                                    placeholder="Enter out points"
                                    name="outPoints"
                                    id="outPoints"
                                    inputType="text"
                                    register={register}
                                    onKeyDown={customNumberInputHandler(5, sessionList?.decimalPoint || 2)}
                                    onChange={handleOutPointsChange}
                                    // value={outPoints || ""} // Ensure empty field when null
                                />
                            </Grid>
                        </Grid>
                    </>)
            }
            handleSubmitBtn={isSubmit ? handleSubmit(saveEditPoints) : handleSubmit(sendEditPointsOtp)}
            disabledSubmitBtn={isLoading || (isSubmit ? otp.length < 5 : !inPoints && !outPoints)}
        />
    );
};

export default EditPointsDialog;
